import {
  useGetUnmappedInvoices,
  useSearchContractsLight,
  useSyncClientInvoice,
  useUpdateClientInvoice,
} from '@actions';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import Table from '@components/V4/Table/Table';
import { useDebounce } from '@hooks';
import { UnmappedInvoiceDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import { CLIENT_INVOICE_STATUS_LABELS } from '@shared/constants';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useState } from 'react';
import { formatDate, formatMoney, invoiceStatusTone } from '../utils';

interface UnmappedInvoicesTableProps {
  onOpenContract: (contractId: string) => void;
}

type RowSelection = { contractId?: string; installmentIndex?: number | null };

const UnmappedInvoicesTable = (props: UnmappedInvoicesTableProps) => {
  const { onOpenContract } = props;
  const toast = useToast();

  const { data: invoices, isLoading } = useGetUnmappedInvoices();
  const [selections, setSelections] = useState<Record<string, RowSelection>>({});
  const [contractSearch, setContractSearch] = useState('');
  const debouncedContractSearch = useDebounce(contractSearch, 400);

  const { data: contractOptions, isLoading: isContractsLoading } = useSearchContractsLight({
    search: debouncedContractSearch,
    limit: 20,
  });

  const { mutate: updateInvoice, isLoading: isUpdating } = useUpdateClientInvoice({
    onSuccess: () => toast.success('Invoice updated.'),
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'Invoice could not be updated.'),
  });

  const { mutate: syncInvoice, isLoading: isSyncing } = useSyncClientInvoice({
    onSuccess: () => toast.success('Invoice refreshed from SmartBill.'),
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'Invoice could not be refreshed.'),
  });

  const linkInvoice = (
    invoice: UnmappedInvoiceDTO,
    contractId: string,
    installmentIndex?: number | null
  ) => {
    updateInvoice([invoice._id], { contractId, installmentIndex: installmentIndex ?? null });
  };

  const ignoreInvoice = (invoice: UnmappedInvoiceDTO) => {
    updateInvoice([invoice._id], { cancelled: true, note: 'Ignored from the unmapped queue' });
  };

  const selectedContract = (invoice: UnmappedInvoiceDTO) =>
    contractOptions?.find((contract) => contract._id === selections[invoice._id]?.contractId);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-customGrey">
        Invoices discovered in SmartBill that could not be matched automatically to a contract
        installment. Link them here or ignore invoices that are not related to a mentoring contract.
      </p>
      <Table isLoading={isLoading}>
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell>Invoice</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>First seen</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Suggestions</Table.HeaderCell>
            <Table.HeaderCell>Link manually</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body hasResults={!!invoices?.length}>
          {invoices?.map((invoice) => {
            const selection = selections[invoice._id] ?? {};
            const contract = selectedContract(invoice);

            return (
              <Table.Row key={invoice._id}>
                <Table.Cell>
                  <div className="flex flex-col">
                    <span className="font-mono">
                      {invoice.series} {invoice.number}
                    </span>
                    {invoice.syncError ? (
                      <span className="text-xs text-customRed">{invoice.syncError}</span>
                    ) : null}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-col">
                    <span>{formatMoney(invoice.totalAmount, invoice.currency)}</span>
                    <span className="text-xs text-customGrey">
                      paid {formatMoney(invoice.paidAmount, invoice.currency)}
                    </span>
                  </div>
                </Table.Cell>
                <Table.Cell>{formatDate(invoice.firstSeenAt)}</Table.Cell>
                <Table.Cell>
                  <span
                    className={mergeClassNames(
                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                      invoiceStatusTone(invoice.status)
                    )}
                  >
                    {CLIENT_INVOICE_STATUS_LABELS[invoice.status]}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {invoice.candidates.length ? (
                    <div className="flex flex-wrap gap-1">
                      {invoice.candidates.map((candidate) => (
                        <button
                          key={`${candidate.contractId}-${candidate.installmentIndex}`}
                          type="button"
                          disabled={isUpdating}
                          onClick={() =>
                            linkInvoice(invoice, candidate.contractId, candidate.installmentIndex)
                          }
                          className="rounded-full bg-customPrimary/10 px-2 py-0.5 text-xs text-customPrimary hover:bg-customPrimary/20"
                          title={`Installment #${candidate.installmentIndex} due ${formatDate(
                            candidate.dueDate
                          )}`}
                        >
                          {candidate.studentName} · #{candidate.installmentIndex}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-customGrey">No match</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-col gap-2">
                    <Select
                      className="w-56"
                      variant="secondary"
                      value={selection.contractId}
                      showSearch
                      filterOption={() => true}
                      onSearch={setContractSearch}
                      isLoading={isContractsLoading}
                      options={(contractOptions ?? []).map((option) => ({
                        label: `${option.studentName}${
                          option.contractNumber ? ` (${option.contractNumber})` : ''
                        }`,
                        value: option._id,
                      }))}
                      onSelect={(value) =>
                        setSelections((prev) => ({
                          ...prev,
                          [invoice._id]: { contractId: String(value), installmentIndex: null },
                        }))
                      }
                      placeholder="Search contract"
                    />
                    {contract ? (
                      <Select
                        className="w-56"
                        variant="secondary"
                        value={selection.installmentIndex ?? null}
                        options={[
                          { label: 'No installment', value: null },
                          ...contract.installments.map((installment) => ({
                            label: `#${installment.index} · ${formatMoney(
                              installment.amount,
                              contract.currency
                            )} · ${formatDate(installment.dueDate)}${
                              installment.hasInvoice ? ' (has invoice)' : ''
                            }`,
                            value: installment.index,
                          })),
                        ]}
                        onSelect={(value) =>
                          setSelections((prev) => ({
                            ...prev,
                            [invoice._id]: {
                              ...prev[invoice._id],
                              installmentIndex: value === null ? null : Number(value),
                            },
                          }))
                        }
                      />
                    ) : null}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      buttonText="Link"
                      disabled={!selection.contractId || isUpdating}
                      onClick={() =>
                        selection.contractId &&
                        linkInvoice(invoice, selection.contractId, selection.installmentIndex)
                      }
                    />
                    <Button
                      variant="secondary"
                      buttonText="Sync"
                      isLoading={isSyncing}
                      onClick={() => syncInvoice([invoice._id], undefined)}
                    />
                    <Button
                      variant="secondary-red"
                      buttonText="Ignore"
                      disabled={isUpdating}
                      onClick={() => ignoreInvoice(invoice)}
                    />
                    {contract ? (
                      <Button
                        variant="text"
                        buttonText="Open contract"
                        onClick={() => onOpenContract(contract._id)}
                      />
                    ) : null}
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default UnmappedInvoicesTable;
